import React from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { Menu } from 'antd';
import { path } from 'ramda';

import { useIntl } from 'common/config/i18n';
import history from 'app-router/history';
import { UserInfoSelectors } from 'common/config/store';

import {
  administrationItems,
  circulationItems,
  customersItems,
  historisationItems,
  MenuItemConfig,
  MenuKey,
} from './items';

const MainMenuWrapper = styled.div`
  .ant-menu-horizontal {
    border-bottom: 3px ${(props) => (props.theme ? path(['colors', 'primary', 'main'], props.theme) : 'inherit')} solid;
  }
  .ant-menu-horizontal > .ant-menu-item,
  .ant-menu-horizontal > .ant-menu-submenu {
    background-color: rgb(240, 240, 240);
    border-top-left-radius: 4px;
    border-top-right-radius: 4px;
    margin-right: 8px;
  }
  .ant-menu-horizontal > .ant-menu-item:hover,
  .ant-menu-horizontal > .ant-menu-submenu:hover,
  .ant-menu-horizontal > .ant-menu-item-active,
  .ant-menu-horizontal > .ant-menu-submenu-active,
  .ant-menu-horizontal > .ant-menu-item-open,
  .ant-menu-horizontal > .ant-menu-submenu-open,
  .ant-menu-horizontal > .ant-menu-item-selected,
  .ant-menu-horizontal > .ant-menu-submenu-selected {
    color: white !important;
    border-color: ${(props) => (props.theme ? path(['colors', 'primary', 'main'], props.theme) : 'inherit')};
    background-color: ${(props) => (props.theme ? path(['colors', 'primary', 'main'], props.theme) : 'inherit')};
  }
  .ant-menu-item:hover,
  .ant-menu-item-active,
  .ant-menu:not(.ant-menu-inline) .ant-menu-submenu-open,
  .ant-menu-submenu-active,
  .ant-menu-submenu-selected > .ant-menu-submenu-title,
  .ant-menu-submenu-title:hover {
    color: white !important;
  }
  .ant-menu-item,
  .ant-menu-submenu-title {
    text-transform: uppercase;
  }
  .ant-menu-item:first-child {
    margin-left: 16px;
  }
  .ant-menu-submenu:hover:after,
  .ant-menu-item-selected:after,
  .ant-menu-submenu:after,
  .ant-menu-horizontal > .ant-menu-item:after {
    border-bottom: 0 !important;
  }
`;

const MainMenu: React.FC = () => {
  const { t } = useIntl();
  const selectedKey = history.location.pathname.replace('/', '') as MenuKey;

  const canReadCirculations = useSelector(UserInfoSelectors.hasReadAccess('Circulations'));
  const canReadHistorisation = useSelector(UserInfoSelectors.hasReadAccess('Historisation'));
  const canReadCustomerFlux = useSelector(UserInfoSelectors.hasReadAccess('Customer-Flux'));
  const canReadReasons = useSelector(UserInfoSelectors.hasReadAccess('Reasons'));
  const canReadHelpInfo = useSelector(UserInfoSelectors.hasReadAccess('Help-Info'));
  const canReadTypologies = useSelector(UserInfoSelectors.hasReadAccess('Typologies'));
  const canReadSituations = useSelector(UserInfoSelectors.hasReadAccess('Situations'));
  const canReadProfiles = useSelector(UserInfoSelectors.hasReadAccess('Profiles'));

  const clickMenuHandler = ({ key }: { key: string }): void => {
    const castedKey = key as MenuKey;
    if (['customers/OD', 'customers/station', 'users'].includes(castedKey)) {
      history.push(`/${castedKey}`, { fromMenu: true });
    } else {
      history.push(`/${castedKey}`);
    }
  };

  const menuItems: MenuItemConfig[] = [];
  if (canReadCirculations) {
    menuItems.push(circulationItems(t));
  }
  if (canReadHistorisation) {
    menuItems.push(historisationItems(t));
  }
  if (canReadCustomerFlux) {
    menuItems.push(customersItems(t));
  }
  menuItems.push(
    administrationItems(t, {
      canReadHelpInfo: !!canReadHelpInfo,
      canReadSituations: !!canReadSituations,
      canReadReasons: !!canReadReasons,
      canReadTypologies: !!canReadTypologies,
      canReadProfiles: !!canReadProfiles,
    })
  );

  return (
    <MainMenuWrapper>
      <Menu mode="horizontal" selectedKeys={[selectedKey]} onClick={clickMenuHandler} items={menuItems}></Menu>
    </MainMenuWrapper>
  );
};

export default MainMenu;
